import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Field } from '@ontruck/orc';
import PhoneInput from 'components/PhoneInput';

const ES_DIAL = {
  code: 'ES',
  dial: '34'
};

const FR_DIAL = {
  code: 'FR',
  dial: '33'
};

const NL_DIAL = {
  code: 'NL',
  dial: '31'
};

const EN_DIAL = {
  code: 'UK',
  dial: '44'
};

const COUNTRIES = {
  es: ES_DIAL,
  'es-ES': ES_DIAL,
  en: EN_DIAL,
  fr: FR_DIAL,
  'fr-FR': FR_DIAL,
  nl: NL_DIAL,
  'nl-NL': NL_DIAL
};

const UsernameInput = ({
  className,
  value,
  disabled,
  handleSetValue,
  error
}) => {
  const [showPrefix, setShowPrefix] = useState(false);
  const [defaultCountry, setDefaultCountry] = useState(null);
  const { t, i18n } = useTranslation('Login');
  let inputFieldEmailRef = null;

  const trimLeadingZeros = useCallback(val => {
    return val.replace(/^0+/, '');
  }, []);

  const handleUpdateValue = useCallback(
    username => {
      handleSetValue(
        showPrefix
          ? trimLeadingZeros(username)
          : `${defaultCountry.dial}${trimLeadingZeros(username)}`
      );
    },
    [showPrefix, defaultCountry, handleSetValue, trimLeadingZeros]
  );

  useEffect(() => {
    setDefaultCountry(COUNTRIES[i18n.language] || COUNTRIES['es']);
  }, [i18n]);

  useEffect(() => {
    if (!showPrefix) {
      inputFieldEmailRef.focus();
    }
  }, [showPrefix, inputFieldEmailRef]);

  const checkIsTypingLetters = useCallback(
    event => {
      const val = event?.nativeEvent?.data;
      return showPrefix && val && (val.includes('@') || /[a-z]/.test(val));
    },
    [showPrefix]
  );

  const checkIsTypingAPhone = useCallback(val => {
    return (
      /(^\+\d{1})|(^\d{2})/.test(val) &&
      !val.includes('@') &&
      !/[a-z]/.test(val)
    );
  }, []);

  const parseForEmail = useCallback((username, e, country) => {
    const usernameWithLastChar = username + e?.nativeEvent?.data;
    return usernameWithLastChar.replace(country.dialCode, '');
  }, []);

  const handleUsernamePhoneChange = useCallback(
    (username, country, e) => {
      e.persist();
      if (checkIsTypingLetters(e)) {
        setShowPrefix(false);
        handleSetValue(parseForEmail(username, e, country));
      } else {
        setShowPrefix(true);
        handleUpdateValue(username);
      }
    },
    [checkIsTypingLetters, handleSetValue, handleUpdateValue, parseForEmail]
  );

  const handleUsernameInputChange = useCallback(
    username => {
      if (checkIsTypingAPhone(username)) {
        setShowPrefix(true);
        handleUpdateValue(username);
      } else {
        setShowPrefix(false);
        handleSetValue(username);
      }
    },
    [checkIsTypingAPhone, handleUpdateValue, handleSetValue]
  );

  return (
    <div className={className} data-testid="username-input">
      <div className="username-input">
        <label
          className="username-input__label visually-hidden"
          htmlFor="username"
        >
          {t('user')}
        </label>
        {!showPrefix && (
          <Field
            id="username"
            label={t('user')}
            name="username"
            className="username-input__input"
            size="M"
            value={value}
            disabled={disabled}
            onChange={(e, input) => handleUsernameInputChange(input.value)}
            fieldRef={ref => {
              inputFieldEmailRef = ref;
            }}
          />
        )}
        {showPrefix && (
          <div className="username-input__input">
            <PhoneInput
              id="username"
              country={defaultCountry.code}
              value={value}
              onChange={handleUsernamePhoneChange}
              label={t('user')}
              localization={i18n.language}
            />
          </div>
        )}
      </div>
    </div>
  );
};

UsernameInput.defaultProps = {
  disabled: false,
  extraClassName: '',
  error: false
};

UsernameInput.propTypes = {
  className: PropTypes.string,
  value: PropTypes.string.isRequired,
  handleSetValue: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.bool
};
export default UsernameInput;
